import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DefaultProjectSelections } from 'shared/projects/DefaultProjectSelections';
import { ProjectsContext } from 'src/projects/ProjectsContext.ts';
import { Selection } from 'src/projects/types/Selection';
import { SelectionRepository } from 'src/projects/SelectionRepository.ts';
import { convertSelection } from 'src/projects/convertSelection.ts';
import { Project } from 'src/projects/types/Project';

type DefaultProjectState = {
    selectionRepository: SelectionRepository;
    persistCallback: (selections: Selection[]) => Promise<null>;
}

export const useDefaultProject = () => {
    const projectsRepository = useContext(ProjectsContext)();
    const service = useMemo(
        () => new DefaultProjectSelections<Selection, Selection>(projectsRepository, 'Selection', convertSelection),
        [projectsRepository],
    );
    const [state, setState] = useState<DefaultProjectState>({
        selectionRepository: new SelectionRepository([]),
        persistCallback: async () => null,
    });
    const [loading, setLoading] = useState<boolean>(true);
    const updateState = useCallback((p: Project) => {
        setState({
            selectionRepository: new SelectionRepository(p.selections),
            persistCallback: async (selections: Selection[]) => {
                const projectUpdated = await service.persist(p, selections);
                updateState(projectUpdated);
                return null;
            },
        });
    }, [service]);

    useEffect(() => {
        setLoading(true);
        service.load()
            .then((p) => {
                updateState(p);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [service, updateState]);
    return { state, loading };
};
